import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { setLastApprenantId } from '../../../lib/useLastApprenantId.js';
import { ArrowLeft, Info, User, Activity, Database, FlaskConical, UserPlus } from 'lucide-react';
import { IdentiteCard } from '../slices/IdentiteCard.js';
import { TimelineCard } from '../slices/TimelineCard.js';
import { QualiteCard } from '../../qualiopi/slices/QualiteCard.js';
import { CommunicationsCard } from '../../communications/slices/CommunicationsCard.js';
import { NotesPanel } from '../../notes/slices/NotesPanel.js';
import { PedagogieCard } from '../../pedagogie/slices/PedagogieCard.js';
import { DocumentsCard } from '../../documents/slices/DocumentsCard.js';
import { ContratCard } from '../../contractualisation/slices/ContratCard.js';
import { FinancierCard } from '../../finance/slices/FinancierCard.js';
import { useApprenant } from '../hooks/useApprenant.js';
import { NouvelApprenantModal } from '../components/NouvelApprenantModal.js';

/**
 * Fiche Apprenant V2 — Page ORCHESTRATEUR (itération 3)
 *
 *   ┌──────────────────────────────────────────────────────────┐
 *   │  TimelineCard (pleine largeur) — pipeline + next action  │
 *   ├──────────────────────────┬───────────────────────────────┤
 *   │  STATIQUE — Profil élève │  DYNAMIQUE — Actions OF        │
 *   │  (qui il est + ce qu'il  │  (ce qui bouge + ce que je    │
 *   │   a signé)               │   fais pour lui)              │
 *   │                          │                               │
 *   │  · IdentiteCard          │  · QualiteCard ⌄              │
 *   │  · ContratCard (à venir) │  · PedagogieCard ⌄            │
 *   │                          │  · DocumentsCard ⌄            │
 *   │                          │  · CommunicationsCard ⌄       │
 *   │                          │  · FinancierCard (à venir)    │
 *   └──────────────────────────┴───────────────────────────────┘
 *
 * Iter 3 (Rafik 27/05/2026) :
 *  - Pédagogie réalisée + Documents BASCULÉS à droite (dynamiques)
 *  - Tous les slices droite passent en mode "collapsible" (résumé + détail)
 *  - Seule Identité reste statique en lecture directe (carte d'identité)
 */
export function ApprenantDetailPage() {
  const { id = 'demo-martine' } = useParams<{ id: string }>();
  // Hook qui sert aussi à l'IdentiteCard — ici on l'utilise juste pour
  // afficher la source (api réel ou mock fallback) dans le header.
  const { data: apprenant } = useApprenant(id);
  const source = apprenant?.source;
  const [createOpen, setCreateOpen] = useState(false);

  // Mémorise le dernier apprenant consulté (sauf la démo) pour que le
  // clic sur "Apprenants" dans la sidebar nous y ramène plutôt qu'à Martine.
  useEffect(() => {
    setLastApprenantId(id);
  }, [id]);

  return (
    <div className="mx-auto max-w-7xl">
      {/* Header */}
      <div className="mb-5 flex items-center gap-3">
        <button className="rounded-lg p-2 text-neutral-500 hover:bg-white">
          <ArrowLeft className="h-4 w-4" />
        </button>
        <div className="flex-1">
          <div className="text-xs font-semibold uppercase tracking-wider text-neutral-500">
            Fiche apprenant
          </div>
          <h1 className="text-2xl font-bold text-neutral-900">
            {apprenant ? `${apprenant.prenom} ${apprenant.nom}` : 'Chargement…'}
          </h1>
        </div>
        {/* Badge source — visible pour ne pas confondre démo et réel */}
        {source && (
          <span
            className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider ${
              source === 'api'
                ? 'border border-emerald-300 bg-emerald-50 text-emerald-700'
                : 'border border-amber-300 bg-amber-50 text-amber-700'
            }`}
          >
            {source === 'api' ? (
              <>
                <Database className="h-3 w-3" /> Données réelles Zoho
              </>
            ) : (
              <>
                <FlaskConical className="h-3 w-3" /> Mock (fallback)
              </>
            )}
          </span>
        )}
        {/* CTA primaire — Nouvel apprenant (anciennement dans la sidebar) */}
        <button
          type="button"
          onClick={() => setCreateOpen(true)}
          className="bg-primary-600 hover:bg-primary-700 inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-xs font-semibold text-white shadow-sm transition-colors"
        >
          <UserPlus className="h-4 w-4" />
          Nouvel apprenant
        </button>
      </div>

      {/* Modal création — couvre toute la zone Apprenants */}
      <NouvelApprenantModal open={createOpen} onClose={() => setCreateOpen(false)} />

      {/* Notice méthodo */}
      <div className="border-primary-200 bg-primary-50 text-primary-900 mb-5 flex items-start gap-3 rounded-lg border p-3 text-xs">
        <Info className="text-primary-600 mt-0.5 h-4 w-4 flex-shrink-0" />
        <div>
          <strong>Phase wiring 1.</strong> Identité branchée sur la VRAIE API Zoho (badge vert
          au-dessus). Les autres slices restent en mock le temps que les endpoints backend soient
          créés (badge orange = fallback mock automatique si API indisponible).
        </div>
      </div>

      {/* TIMELINE (pleine largeur) */}
      <div className="mb-5">
        <TimelineCard apprenantId={id} />
      </div>

      {/* GRID 2 COLONNES */}
      <div className="grid gap-5 lg:grid-cols-2">
        {/* ── COLONNE GAUCHE — STATIQUE ── */}
        <div className="space-y-5">
          <ColumnHeader
            icon={<User className="h-4 w-4 text-emerald-600" />}
            title="Profil élève"
            subtitle="Qui il est · ce qu'il a signé"
            color="emerald"
          />
          <IdentiteCard apprenantId={id} />
          <ContratCard apprenantId={id} />
        </div>

        {/* ── COLONNE DROITE — DYNAMIQUE ── */}
        <div className="space-y-5">
          <ColumnHeader
            icon={<Activity className="text-primary-600 h-4 w-4" />}
            title="Actions OF"
            subtitle="Ce qui bouge · ce que je fais pour lui"
            color="primary"
          />
          <QualiteCard apprenantId={id} />
          <PedagogieCard apprenantId={id} />
          <DocumentsCard apprenantId={id} />
          <CommunicationsCard apprenantId={id} />
          <NotesPanel apprenantId={id} />
          <FinancierCard apprenantId={id} />
        </div>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────

function ColumnHeader({
  icon,
  title,
  subtitle,
  color,
}: {
  icon: React.ReactNode;
  title: string;
  subtitle: string;
  color: 'emerald' | 'primary';
}) {
  const cls =
    color === 'emerald' ? 'border-emerald-200 bg-emerald-50' : 'border-primary-200 bg-primary-50';
  return (
    <div className={`flex items-center gap-3 rounded-lg border ${cls} px-3 py-2`}>
      {icon}
      <div>
        <div className="text-sm font-bold text-neutral-900">{title}</div>
        <div className="text-[10px] text-neutral-600">{subtitle}</div>
      </div>
    </div>
  );
}
